// PRD 06 §2.1 / §6 / §8-1 — area-grouped item table inside a module card.
//
//   - Slice 6: read-only structure (rowspan'd area cell, item numbering,
//     subtotals/total).
//   - Slice 7: ✕ remove button per row (canWrite-gated).
//   - Slice 8: native HTML5 drag-and-drop. Area grips reorder areas inside
//     the module; item grips reorder items within the same area. Drop
//     indicators (boxShadow insets) match the prototype's
//     `.mc-area-row--drop-before` / `--drop-after`.
//
// Cross-module drag is not supported (PRD 06 §6).

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';

import { DirtyBadge } from '@/components/feedback/DirtyBadge';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import { buildModuleAreaBreakdown } from '../utils/area-group';
import type { Module, ProItemRef } from '../types/pro-deployment.types';

interface AreaItemTableProps {
  module: Module;
  itemDict: ReadonlyMap<string, ProItemRef>;
  canWrite: boolean;
  onRequestRemove: ((item: ProItemRef) => void) | null;
  // Slice 8 — drag handlers. null means reorder is disabled (read-only or
  // single-area / single-item module).
  onReorderAreas:
    | ((sourceArea: string, targetArea: string, placeAfter: boolean) => void)
    | null;
  onReorderItems:
    | ((sourceItemId: string, targetItemId: string, placeAfter: boolean) => void)
    | null;
  // FEATURE_025 — whether an item is shown on this row for reference only (조회 전용). Omitted → none is.
  isViewOnly?: (item: ProItemRef) => boolean;
}

type DragState =
  | { mode: 'area'; area: string }
  | { mode: 'item'; itemId: string; area: string }
  | null;
type DropTarget =
  | { mode: 'area'; area: string; after: boolean }
  | { mode: 'item'; itemId: string; after: boolean }
  | null;

const COL_AREA_WIDTH = '36%';
const COL_QUESTIONS_WIDTH = 72;
const COL_DELETE_WIDTH = 60;

const thBaseStyle = {
  textAlign: 'left' as const,
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: '2px solid var(--border)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--muted-foreground)',
  fontWeight: 600,
};

const tdBaseStyle = {
  padding: '6px var(--space-3)',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle' as const,
  wordBreak: 'break-word' as const,
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1.4,
};

const subtotalRowStyle = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--muted-foreground)',
  borderBottom: '2px solid var(--border)',
};

const totalRowStyle = {
  fontWeight: 700,
  borderTop: '2px solid var(--border)',
};

// Drop indicators — inset shadow on the first cell. Matches the prototype's
// `.mc-area-row--drop-before` (3px top) and `--drop-after` (3px bottom).
function indicatorBoxShadow(position: 'before' | 'after', thick: number): string {
  return position === 'before'
    ? `inset 0 ${thick}px 0 0 var(--info)`
    : `inset 0 -${thick}px 0 0 var(--info)`;
}

export function AreaItemTable({
  module,
  itemDict,
  canWrite,
  onRequestRemove,
  onReorderAreas,
  onReorderItems,
  isViewOnly,
}: AreaItemTableProps) {
  const { t } = useTranslation('pro-deployment');
  const { denyProps, tooltip } = useWriteDenyGuard(canWrite);
  const breakdown = useMemo(
    () => buildModuleAreaBreakdown(module, itemDict),
    [module, itemDict],
  );

  const [drag, setDrag] = useState<DragState>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);
  // SHCT-619 — the area name cell is a single `rowSpan`'d <td> covering the whole
  // group, so it is the only element whose rect matches what the admin sees as
  // "the area". Every row of the group is a drop target (below), but before/after
  // must be decided against this block, not against whichever row the cursor
  // happens to be over — otherwise the lower rows of a tall area always read as
  // "after" and the drop lands one slot off.
  const areaCellRefs = useRef(new Map<string, HTMLTableCellElement>());

  const areaBlockRect = (area: string, fallback: HTMLElement): DOMRect =>
    areaCellRefs.current.get(area)?.getBoundingClientRect() ??
    fallback.getBoundingClientRect();

  const resetDrag = () => {
    setDrag(null);
    setDropTarget(null);
  };

  if (breakdown.groups.length === 0) {
    // ADM.011 — an empty module blocks deployment; surface the reason here,
    // in the module's own area, so the admin knows what to fix before deploy.
    return (
      <div
        role="alert"
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--badge-warning-text)',
          background: 'var(--badge-warning-bg)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-2) var(--space-3)',
        }}
      >
        {t('module.emptyDeployBlock')}
      </div>
    );
  }

  const areasDraggable = canWrite && !!onReorderAreas && breakdown.groups.length > 1;

  const handleAreaDragStart = (area: string) => (e: React.DragEvent) => {
    if (!areasDraggable) return;
    setDrag({ mode: 'area', area });
    setDropTarget(null);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require setData to actually start the drag.
    try {
      e.dataTransfer.setData('text/plain', area);
    } catch {
      /* noop */
    }
  };

  const handleItemDragStart = (item: ProItemRef) => (e: React.DragEvent) => {
    if (!canWrite || !onReorderItems) return;
    setDrag({ mode: 'item', itemId: item.pid, area: item.area || '미분류' });
    setDropTarget(null);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', item.pid);
    } catch {
      /* noop */
    }
  };

  const setDropIfChanged = (next: DropTarget) => {
    setDropTarget((prev) => {
      if (!next && !prev) return prev;
      if (!next || !prev) return next;
      if (next.mode !== prev.mode || next.after !== prev.after) return next;
      if (next.mode === 'area' && prev.mode === 'area' && next.area === prev.area) return prev;
      if (next.mode === 'item' && prev.mode === 'item' && next.itemId === prev.itemId)
        return prev;
      return next;
    });
  };

  const handleAreaDragOver =
    (area: string) => (e: React.DragEvent<HTMLTableRowElement>) => {
      if (drag?.mode !== 'area') return;
      if (drag.area === area) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const rect = areaBlockRect(area, e.currentTarget);
      const after = e.clientY - rect.top > rect.height / 2;
      setDropIfChanged({ mode: 'area', area, after });
    };

  const handleItemDragOver =
    (item: ProItemRef) => (e: React.DragEvent<HTMLTableRowElement>) => {
      if (drag?.mode !== 'item') return;
      const itemArea = item.area || '미분류';
      // Same module is implicit (state is per-table); same area only.
      if (drag.area !== itemArea) return;
      if (drag.itemId === item.pid) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const after = e.clientY - rect.top > rect.height / 2;
      setDropIfChanged({ mode: 'item', itemId: item.pid, after });
    };

  const handleAreaDrop =
    (area: string) => (e: React.DragEvent<HTMLTableRowElement>) => {
      if (drag?.mode !== 'area') return;
      if (drag.area === area) {
        resetDrag();
        return;
      }
      e.preventDefault();
      const rect = areaBlockRect(area, e.currentTarget);
      const after = e.clientY - rect.top > rect.height / 2;
      onReorderAreas?.(drag.area, area, after);
      resetDrag();
    };

  const handleItemDrop =
    (item: ProItemRef) => (e: React.DragEvent<HTMLTableRowElement>) => {
      if (drag?.mode !== 'item') return;
      const itemArea = item.area || '미분류';
      if (drag.area !== itemArea || drag.itemId === item.pid) {
        resetDrag();
        return;
      }
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const after = e.clientY - rect.top > rect.height / 2;
      onReorderItems?.(drag.itemId, item.pid, after);
      resetDrag();
    };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 'var(--font-size-sm)',
        tableLayout: 'fixed',
      }}
    >
      <colgroup>
        <col style={{ width: COL_AREA_WIDTH }} />
        <col />
        <col style={{ width: COL_QUESTIONS_WIDTH }} />
        <col style={{ width: COL_DELETE_WIDTH }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thBaseStyle}>{t('module.tableHeaders.area')}</th>
          <th style={thBaseStyle}>{t('module.tableHeaders.item')}</th>
          <th style={{ ...thBaseStyle, textAlign: 'center' }}>
            {t('module.tableHeaders.questions')}
          </th>
          <th style={{ ...thBaseStyle, textAlign: 'center' }}>
            {t('module.tableHeaders.delete')}
          </th>
        </tr>
      </thead>
      <tbody onDragEnd={resetDrag}>
        {breakdown.groups.map((group, areaIdx) => {
          const itemsDraggable =
            canWrite && !!onReorderItems && group.items.length > 1;
          const areaIsBeingDragged = drag?.mode === 'area' && drag.area === group.name;
          const areaDropForThisArea =
            dropTarget?.mode === 'area' && dropTarget.area === group.name;

          return group.items.flatMap((item, itemIdx) => {
            const itemIsBeingDragged =
              drag?.mode === 'item' && drag.itemId === item.pid;
            const itemDropForThisItem =
              dropTarget?.mode === 'item' && dropTarget.itemId === item.pid;
            const isFirstInArea = itemIdx === 0;

            const areaCellShadow =
              isFirstInArea && areaDropForThisArea
                ? indicatorBoxShadow(dropTarget.after ? 'after' : 'before', 3)
                : undefined;
            const itemCellShadow = itemDropForThisItem
              ? indicatorBoxShadow(dropTarget.after ? 'after' : 'before', 2)
              : undefined;

            const opacity = areaIsBeingDragged || itemIsBeingDragged ? 0.4 : 1;

            const rows = [
              <tr
                key={item.pid}
                style={{ opacity }}
                // SHCT-619 — an area drop is accepted on EVERY row of the group, not
                // only its first. The area cell is `rowSpan`'d, so gating on
                // `isFirstInArea` left the item/count/delete columns of rows 2..n as a
                // dead zone: no dragover there meant no preventDefault, so the browser
                // rejected the drop and the area sprang back.
                onDragOver={
                  areasDraggable && drag?.mode === 'area'
                    ? handleAreaDragOver(group.name)
                    : itemsDraggable && drag?.mode === 'item'
                      ? handleItemDragOver(item)
                      : undefined
                }
                onDrop={
                  areasDraggable && drag?.mode === 'area'
                    ? handleAreaDrop(group.name)
                    : itemsDraggable && drag?.mode === 'item'
                      ? handleItemDrop(item)
                      : undefined
                }
              >
                {isFirstInArea ? (
                  <td
                    rowSpan={group.items.length}
                    ref={(el) => {
                      if (el) areaCellRefs.current.set(group.name, el);
                      else areaCellRefs.current.delete(group.name);
                    }}
                    style={{
                      ...tdBaseStyle,
                      fontWeight: 700,
                      boxShadow: areaCellShadow,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px',
                        margin: '-4px -4px -4px -8px',
                        cursor: areasDraggable ? 'grab' : 'default',
                        borderRadius: 'var(--radius-sm)',
                        userSelect: 'none',
                      }}
                      draggable={areasDraggable}
                      onDragStart={areasDraggable ? handleAreaDragStart(group.name) : undefined}
                      title={areasDraggable ? t('module.reorder.area') : undefined}
                      role={areasDraggable ? 'button' : undefined}
                      aria-label={areasDraggable ? t('module.reorder.area') : undefined}
                      aria-grabbed={
                        areasDraggable
                          ? drag?.mode === 'area' && drag.area === group.name
                          : undefined
                      }
                    >
                      {areasDraggable ? (
                        <span
                          aria-hidden
                          style={{
                            display: 'inline-flex',
                            color: 'var(--muted-foreground)',
                            flexShrink: 0,
                          }}
                        >
                          <GripVertical size={14} />
                        </span>
                      ) : null}
                      <span>
                        <span style={{ fontWeight: 700, marginRight: 2 }}>
                          {areaIdx + 1}.
                        </span>{' '}
                        {group.name}
                      </span>
                    </span>
                  </td>
                ) : null}
                <td
                  style={{
                    ...tdBaseStyle,
                    boxShadow: itemCellShadow,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {itemsDraggable ? (
                      <span
                        draggable
                        onDragStart={handleItemDragStart(item)}
                        title={t('module.reorder.item')}
                        role="button"
                        aria-label={t('module.reorder.item')}
                        aria-grabbed={drag?.mode === 'item' && drag.itemId === item.pid}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          cursor: 'grab',
                          padding: '2px 4px',
                          margin: '-2px 4px -2px -4px',
                          borderRadius: 4,
                          userSelect: 'none',
                          color: 'var(--muted-foreground)',
                        }}
                      >
                        <GripVertical size={12} aria-hidden />
                      </span>
                    ) : null}
                    <span
                      style={{
                        color: 'var(--muted-foreground)',
                        marginRight: 4,
                        fontSize: 'var(--font-size-xs)',
                      }}
                    >
                      {itemIdx + 1})
                    </span>
                    <span>{item.name}</span>
                    {/* SHCT-728 — PRD 06 §2.2 No.2-2 「항목 행 미배포 배지」: this is the layer that
                        reveals WHICH item came in this round, which the header chip never could.
                        Read off the item the server sent, not the browser's session store — see the
                        note on the card badge in ModuleCard. */}
                    {item.dirty ? (
                      <DirtyBadge
                        style={{ marginLeft: 6 }}
                        title={t('module.undeployed.tooltip')}
                      />
                    ) : null}
                    {/* FEATURE_025 — PRD 06 v1.21 §3.1: a code row deploys only its own items, so the common and
                        group items it shows are marked as reference-only, with where to deploy them. */}
                    {isViewOnly?.(item) ? (
                      <span
                        className="badge"
                        style={{
                          marginLeft: 6,
                          background: 'var(--secondary)',
                          color: 'var(--secondary-foreground)',
                        }}
                        title={t('module.viewOnly.tooltip')}
                      >
                        {t('module.viewOnly.badge')}
                      </span>
                    ) : null}
                  </span>
                </td>
                <td style={{ ...tdBaseStyle, textAlign: 'center' }}>{item.questionCount}</td>
                <td style={{ ...tdBaseStyle, textAlign: 'center' }}>
                  {/* SHCT-582 — PRD 21 §7.4 P-2: the ✕ stays visible without PRO_MGMT:WRITE and
                      goes `disabled` + 툴팁. It used to disappear, leaving an unexplained empty
                      column under a 삭제 header. The hover repaint is skipped while denied so a
                      disabled control never lights up destructive-red under the cursor. */}
                  {onRequestRemove ? (
                    <button
                      type="button"
                      aria-label={t('module.tableHeaders.delete')}
                      onClick={() => onRequestRemove(item)}
                      disabled={!canWrite}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: canWrite ? 'pointer' : 'not-allowed',
                        opacity: canWrite ? undefined : 0.5,
                        color: 'var(--muted-foreground)',
                        fontSize: 'var(--font-size-xs)',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!canWrite) return;
                        const el = e.currentTarget as HTMLButtonElement;
                        el.style.background = 'var(--destructive)';
                        el.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        if (!canWrite) return;
                        const el = e.currentTarget as HTMLButtonElement;
                        el.style.background = 'none';
                        el.style.color = 'var(--muted-foreground)';
                      }}
                      {...denyProps}
                      title={tooltip ?? t('module.tableHeaders.delete')}
                    >
                      ✕
                    </button>
                  ) : null}
                </td>
              </tr>,
            ];

            if (itemIdx === group.items.length - 1 && group.items.length >= 2) {
              rows.push(
                <tr
                  key={`${group.name}__subtotal`}
                  style={subtotalRowStyle}
                  // SHCT-619 — the subtotal row is rendered inside the group block, so
                  // it must accept an area drop like any other row of the group.
                  onDragOver={
                    areasDraggable && drag?.mode === 'area'
                      ? handleAreaDragOver(group.name)
                      : undefined
                  }
                  onDrop={
                    areasDraggable && drag?.mode === 'area'
                      ? handleAreaDrop(group.name)
                      : undefined
                  }
                >
                  <td style={tdBaseStyle}></td>
                  <td style={{ ...tdBaseStyle, textAlign: 'right' }}>{t('module.subtotal')}</td>
                  <td style={{ ...tdBaseStyle, textAlign: 'center' }}>
                    {group.totalQuestions}
                  </td>
                  <td style={tdBaseStyle}></td>
                </tr>,
              );
            }
            return rows;
          });
        })}
      </tbody>
      <tfoot>
        <tr style={totalRowStyle}>
          <td colSpan={2} style={tdBaseStyle}></td>
          <td
            style={{
              ...tdBaseStyle,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                color: 'var(--muted-foreground)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                marginRight: 4,
              }}
            >
              {t('module.total')}
            </span>
            <strong>{breakdown.totalQuestions}</strong>
          </td>
          <td style={tdBaseStyle}></td>
        </tr>
      </tfoot>
    </table>
  );
}
